import { ReactNode } from 'react';
import { Badge, Body1, Caption1, makeStyles, tokens } from '@fluentui/react-components';
import { LinkRegular } from '@fluentui/react-icons';

import { getSourceRecordById } from '../../data';
import { TChangeV2 } from '../../models';
import { formatDate } from '../../utils';
import { CriticalityBadge } from '../shared/CriticalityBadge';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXXL}`,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  item: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '120px' },
  label: { color: tokens.colorNeutralForeground3 },
});

const MetaItem = ({ label, children }: { label: string; children: ReactNode }) => {
  const classes = useStyles();
  return (
    <div className={classes.item}>
      <Caption1 className={classes.label}>{label}</Caption1>
      {children}
    </div>
  );
};

export const ChangeMetadata = ({ change }: { change: TChangeV2 }) => {
  const classes = useStyles();
  const source = getSourceRecordById(change.sourceRecord);

  return (
    <div className={classes.root}>
      <MetaItem label="Change manager">
        <Body1>{change.changeManager ?? 'Unassigned'}</Body1>
      </MetaItem>
      <MetaItem label="Criticality">
        <CriticalityBadge criticality={change.criticality} />
      </MetaItem>
      <MetaItem label="Target date">
        <Body1>{formatDate(change.targetDate)}</Body1>
      </MetaItem>
      <MetaItem label="Affected products">
        <Body1>{change.affectedProducts.length > 0 ? change.affectedProducts.join(', ') : 'None'}</Body1>
      </MetaItem>
      <MetaItem label="CAPA">
        {change.capaRequired || change.capaId ? (
          <Badge appearance="tint" color="warning">
            {change.capaId ? `Linked · ${change.capaId}` : 'Linked'}
          </Badge>
        ) : (
          <Body1>Not linked</Body1>
        )}
      </MetaItem>
      <MetaItem label="Source record">
        {source ? (
          <Badge appearance="outline" color="informative" icon={<LinkRegular />}>
            {source.reference}
          </Badge>
        ) : (
          <Body1>None</Body1>
        )}
      </MetaItem>
    </div>
  );
};
